import useWidth from '@components/Hooks/useWidth';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import classNames from 'classnames';
import { ChevronDown, CircleAlert, File, Package, RefreshCw, Star } from 'lucide-react';
import { memo, useState } from 'react';
import LangEnum from '../../../../../../../../../../lang/LangEnum';
import useTeceoStyles from '../../../../../../../../../../useTeceoStyles';
import { ProductGridTestIds } from '../../../../../../../../../../utils/testIds/productGrid.testIds';
import { AvailabilityEnum } from '../../../../../../../../../products/ProductTypes';
import useSharedEntities from '../../../../../../../../../shared-entities/hooks/useSharedEntities';
import { useTranslation } from '../../../../../../../../../translation/TranslationContext';
import CopyOrPasteButton from '../../../../../../components/copy-paste/CopyOrPasteButton';
import { CopyOrPasteStatusEnum } from '../../../../../../components/copy-paste/copyPaste.types';
import { getCopyOrPasteButtonStatus } from '../../../../../../components/copy-paste/copyPaste.utils';
import useOrderProductGridContext from '../../../../../contexts/OrderProductGrid.context';
import { useStoreBackedGrid } from '../../../../../store/useStoreBackedGrid';
import OrderProductPacksMenu from '../../../../packsMenu/components/OrderProductPacksMenu';
import SelloutMenu from '../../../../selloutMenu/components/SelloutMenu';
import { useOrderProductGridButtons } from '../hooks/useOrderProductGridButtons';
import { getGridButtonSx } from '../styles/orderProductGridButton.styles';
import useOrderProductGridButtonStyles from '../styles/useOrderProductGridButtonStyles';
import { OrderProductGridMenuOption } from '../types/OrderProductGrid.types';
import OrderProductGridButtonErrorTooltip from './OrderProductGridButtonErrorTooltip';
import OrderProductGridMenu from './OrderProductGridMenu';

interface OrderProductGridButtonProps {
  handleHoverSugestionButton: (eventType: string) => void;
}

const resolveGridButtonSize = (mediumDownWidth: boolean) => (mediumDownWidth ? 'large' : 'medium');

const OrderProductGridButton = ({ handleHoverSugestionButton }: OrderProductGridButtonProps) => {
  const { classes: teceoClasses } = useTeceoStyles();
  const { classes } = useOrderProductGridButtonStyles();
  const { mediumDownWidth } = useWidth();

  const { translate } = useTranslation();
  const { isPublicAccess } = useSharedEntities({});

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const {
    grid,
    product,
    orderId,
    readMode,
    disableAll,
    copiedGrid,
    hasValidPacks,
    hasSelectedPacks,
    hasSuggestedGrid,
    showSuggestedGrid,
    suggestionAchieved,
    selloutSummary,
    isOrderProductFromKit,
    validationErrors,
    copyGrid,
    handleClickPaste,
    removeCopiedGrid,
  } = useOrderProductGridContext();

  const storeBackedGrid = useStoreBackedGrid({ combinationKey: product.combinationKey, grid });

  const hasValidationErrors = !!validationErrors?.length;

  const {
    openPacksMenu,
    showPacksButton,
    packsButtonLabel,
    openSelloutMenu,
    handleClickPacks,
    handleClosePacks,
    handleClickApplySuggestion,
    handleOpenSellout,
    handleCloseSellout,
  } = useOrderProductGridButtons();
  const showSuggestedButton =
    hasSuggestedGrid && !isPublicAccess && showSuggestedGrid && !readMode && !isOrderProductFromKit;
  const hasPacksMenu = hasValidPacks && openPacksMenu;

  const options: OrderProductGridMenuOption[] = [
    {
      label: LangEnum.ORDER_PRODUCT_GRID_SUGGESTED_ITEM_LABEL,
      icon: <Icon icon={Star} size="md" />,
      onClick: handleClickApplySuggestion,
      active: showSuggestedButton,
      disabled: suggestionAchieved,
      disabledMessage: LangEnum.ORDER_PRODUCT_GRID_SUGGESTED_ITEM_DISABLED_MESSAGE,
      onHover: (event) => handleHoverSugestionButton(event.type),
    },
    {
      label: LangEnum.ORDER_PRODUCT_GRID_SELLOUT_ITEM_LABEL,
      icon: <Icon icon={RefreshCw} size="md" />,
      onClick: handleOpenSellout,
      active: !!selloutSummary?.hasSelloutValues,
    },
    {
      label: readMode
        ? LangEnum.ORDER_PRODUCT_GRID_PACK_ITEM_FINISHED_ORDER_LABEL
        : LangEnum.ORDER_PRODUCT_GRID_PACK_ITEM_LABEL,
      icon: <Icon icon={Package} size="md" />,
      onClick: handleClickPacks,
      active: showPacksButton,
    },
    {
      label: LangEnum.ORDER_PRODUCTS_GRID_STATUS_TO_COPIED,
      icon: <Icon icon={File} size="md" />,
      onClick: () => copyGrid?.({ grid: storeBackedGrid, product, orderId }),
      active: !!copyGrid && !readMode && !isPublicAccess && (!disableAll || isOrderProductFromKit),
      testId: ProductGridTestIds.GRID_MENU_COPY,
    },
  ];

  const copyOrPaste: CopyOrPasteStatusEnum = getCopyOrPasteButtonStatus({
    copiedGrid,
    product,
    grid: storeBackedGrid,
    orderId,
  });

  if (copyOrPaste !== CopyOrPasteStatusEnum.ON_COPY_ACTION) {
    return (
      <CopyOrPasteButton
        copyOrPaste={copyOrPaste}
        disablePaste={isOrderProductFromKit}
        onClickPaste={handleClickPaste}
        removeCopiedGrid={removeCopiedGrid}
        availability={product.availability as AvailabilityEnum}
        allowChangeImmediateGrid={product.productAllowChangeImmediateGrid}
        allowChangePreOrderGrid={product.productAllowChangePreOrderGrid}
      />
    );
  }

  const showButton = options.some((opt) => opt.active);

  if (!showButton) {
    return null;
  }

  const gridButtonEndIcon = hasValidationErrors ? (
    <Icon icon={CircleAlert} size="xs" className={classes.errorIcon} />
  ) : (
    <Icon
      icon={ChevronDown}
      size="sm"
      className={classNames({
        [classes.icon]: true,
        [teceoClasses.verticalFlip]: Boolean(anchorEl),
      })}
    />
  );

  const gridButton = (
    <Button
      variant="contained"
      color="secondary"
      size={resolveGridButtonSize(mediumDownWidth)}
      sx={getGridButtonSx({ hasSelectedPacks })}
      tabIndex={-1}
      onClick={(e) => setAnchorEl(e.currentTarget)}
      endIcon={gridButtonEndIcon}
      testId={ProductGridTestIds.GRID_MENU_BUTTON}
    >
      <span className={teceoClasses.truncate}>
        {hasSelectedPacks
          ? packsButtonLabel
          : translate(LangEnum.ORDER_PRODUCTS_GROUPY_BY_GRID_LABEL)}
      </span>
    </Button>
  );

  return (
    <>
      <OrderProductGridButtonErrorTooltip validationErrors={validationErrors}>
        {gridButton}
      </OrderProductGridButtonErrorTooltip>

      <OrderProductGridMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        options={options}
      />
      {selloutSummary?.hasSelloutValues && openSelloutMenu && (
        <SelloutMenu
          readMode={readMode}
          isOpen={openSelloutMenu}
          onClose={handleCloseSellout}
          orderId={orderId}
          productColorId={product.productColorId}
        />
      )}

      {hasPacksMenu && (
        <OrderProductPacksMenu open={openPacksMenu} handleClose={handleClosePacks} />
      )}
    </>
  );
};

export default memo(OrderProductGridButton);
